import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./signup.css";

export default function SignUp() {
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState("Admin"); // Defaulted to Author/Admin
  const [kraPinFile, setKraPinFile] = useState(null);
  const [copyrightCertificate, setCopyrightCertificate] = useState(null);
  const [originalWorkConfirmation, setOriginalWorkConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  // Handler for KRA PIN file upload
  const handleKraPinChange = (e) => {
    const file = e.target.files[0];
    setKraPinFile(file);
    console.log("KRA PIN File selected:", file);
  };

  // Handler for Copyright/ID Certificate file upload
  const handleCopyrightCertificateChange = (e) => {
    const file = e.target.files[0];
    setCopyrightCertificate(file);
    console.log("ID/Copyright Certificate selected:", file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (!fname || !lname || !email || !password || !userType || !originalWorkConfirmation) {
      setErrorMessage("Please fill in all required fields and confirm your details.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        "https://panafricanbookexchange-wandererspath.com/api/auth/registeradmin",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fname: fname.trim(),
            lname: lname.trim(),
            email: email.trim(),
            password,
            userType,
          }),
        }
      );

      const raw = await response.text();

      if (raw.startsWith("<!DOCTYPE") || raw.startsWith("<html")) {
        setErrorMessage("Server error: Backend returned invalid response.");
        setLoading(false);
        return;
      }

      const data = JSON.parse(raw);
      setLoading(false);

      if (data.status === "ok") {
        window.localStorage.setItem("token", data.token);
        window.localStorage.setItem("userType", data.userType || userType);
        window.localStorage.setItem("loggedIn", "true");

        // Redirect safely using React Router
        navigate("/admin");
      } else {
        console.error("Server Error:", data.error || "Unknown error");
        setErrorMessage(data.error || "Registration failed. Please try again.");
      }
    } catch (err) {
      console.error("Network Error:", err);
      setErrorMessage("A network error occurred. Please check your connection.");
      setLoading(false);
    }
  };

  return (
    <div className="signup-wrapper">
      <div className="signup-card">
        <div className="signup-header">
          <h2>Author Registration</h2>
          <p>Create your author account to publish and distribute your books</p>
        </div>

        {errorMessage && (
          <div className="signup-alert" role="alert">
            <span className="alert-icon">??</span>
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="signup-form">
          {/* Account Type Selection */}
          <div className="usertype-box">
            <label className="radio-container">
              <input
                type="radio"
                name="UserType"
                value="Admin"
                checked={userType === "Admin"}
                onChange={(e) => setUserType(e.target.value)}
              />
              <span className="radio-mark"></span>
              <span className="radio-label">I am registering as an Author</span>
            </label>
          </div>

          {/* Name Row */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fname">First Name *</label>
              <input
                id="fname"
                type="text"
                className="form-input"
                placeholder="First Name"
                value={fname}
                onChange={(e) => setFname(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lname">Last Name *</label>
              <input
                id="lname"
                type="text"
                className="form-input"
                placeholder="Last Name"
                value={lname}
                onChange={(e) => setLname(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              id="email"
              type="email"
              className="form-input"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div className="form-group">
            <label htmlFor="password">Password *</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="form-input"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "??" : ""}
              </button>
            </div>
          </div>

          {/* File Uploads */}
          <div className="file-upload-section">
            <div className="form-group">
              <label className="file-label">Upload KRA PIN or Tax Certificate</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="kraPin"
                  onChange={handleKraPinChange}
                  accept="image/*,.pdf"
                  className="hidden-file-input"
                />
                <label htmlFor="kraPin" className="file-custom-btn">
                  <span> {kraPinFile ? kraPinFile.name : "Choose File (PDF or Image)"}</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="file-label">Upload National ID / Identification Document</label>
              <div className="file-input-wrapper">
                <input
                  type="file"
                  id="nationalId"
                  onChange={handleCopyrightCertificateChange}
                  accept="image/*,.pdf"
                  className="hidden-file-input"
                />
                <label htmlFor="nationalId" className="file-custom-btn">
                  <span> {copyrightCertificate ? copyrightCertificate.name : "Choose File (PDF or Image)"}</span>
                </label>
              </div>
            </div>
          </div>

          {/* Confirmation Checkbox */}
          <div className="checkbox-group">
            <label className="checkbox-container">
              <input
                type="checkbox"
                name="originalWorkConfirmation"
                checked={originalWorkConfirmation}
                onChange={(e) => setOriginalWorkConfirmation(e.target.checked)}
                required
              />
              <span className="checkbox-mark"></span>
              <span className="checkbox-text">
                I hereby confirm that all details provided above are accurate and truthful to the best of my knowledge.
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`btn-submit ${loading ? "is-loading" : ""}`}
          >
            {loading ? (
              <span className="button-loader-content">
                <span className="spinner"></span> Registering...
              </span>
            ) : (
              "Complete Registration"
            )}
          </button>

          {/* Footer Navigation */}
          <div className="signup-footer">
            <p>
              Already registered?{" "}
              <Link to="/admin" className="inline-link font-semibold">
                Sign In
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}